/**
 * AnalysisConfirmationDialog - Confirmation dialog for advanced analysis token consumption
 */

import React, {useState} from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import "../../assets/scss/black-dashboard-react/custom/_analysis-confirmation-modal.scss";
import {useUser} from "../../contexts/AuthContext";
import ReactGA from "react-ga4";
import {GoogleAnalyticsEventCategories} from "../../constants";

interface AnalysisConfirmationDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  tokenCost?: number;
}

const AnalysisConfirmationDialog: React.FC<AnalysisConfirmationDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  tokenCost = 1
}) => {
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const {user} = useUser();
  const userId: string = user.identityId as string;

  const handleConfirm = () => {
    ReactGA.event({
      category: GoogleAnalyticsEventCategories.ANALYSIS,
      action: "confirm run advanced analysis",
      label: userId
    });

    if (dontAskAgain) {
      // Store preference in localStorage
      localStorage.setItem("zwiftalizer_skip_analysis_confirmation", "true");
    }
    onConfirm();
  };

  const handleCancel = () => {
    ReactGA.event({
      category: GoogleAnalyticsEventCategories.ANALYSIS,
      action: "cancel analysis confirmation",
      label: userId
    });
    setDontAskAgain(false); // Reset checkbox on cancel
    onCancel();
  };

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <Modal modalClassName="modal-analysis-confirmation" isOpen={isOpen} toggle={handleCancel}>
          <ModalHeader>
            <button aria-label="Close" className="close" onClick={handleCancel}>
              <i className="fa fa-times" />
            </button>
          </ModalHeader>
          <ModalBody>
            <Card>
              <CardHeader data-color={color}>
                <CardTitle tag="h3">
                  <i className="fa fa-chart-line mr-2"></i>
                  Confirm Advanced Analysis
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="alert alert-info mb-3">
                  <i className="fa fa-info-circle mr-2"></i>
                  <strong>Token Cost:</strong> This analysis will consume <strong>{tokenCost}</strong>{" "}
                  {tokenCost === 1 ? "token" : "tokens"}.
                </div>

                <p>
                  Advanced Analysis performs over 50 checks to detect performance issues and provide actionable
                  recommendations for:
                </p>

                <ul className="mb-3">
                  <li>
                    <strong>Graphics:</strong> FPS drops, CPU bottlenecks, settings optimization
                  </li>
                  <li>
                    <strong>Sensors:</strong> BLE/ANT+ disconnects, power meter stability
                  </li>
                  <li>
                    <strong>Network:</strong> Connectivity issues, packet loss, firewall problems
                  </li>
                  <li>
                    <strong>Hardware:</strong> Compatibility issues, upgrade recommendations
                  </li>
                </ul>

                <FormGroup check className="form-check mb-3">
                  <Label check className="form-check-label">
                    <Input
                      type="checkbox"
                      checked={dontAskAgain}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        ReactGA.event({
                          category: GoogleAnalyticsEventCategories.ANALYSIS,
                          action: "toggle dont ask again",
                          label: `${userId} - ${checked ? "checked" : "unchecked"}`
                        });
                        setDontAskAgain(checked);
                      }}
                    />
                    <span className="form-check-sign"></span>
                    Don't ask me again
                  </Label>
                  <small className="form-text text-muted analysis-checkbox-help">
                    You can reset this preference in Settings
                  </small>
                </FormGroup>

                <Button color="primary" data-color={color} onClick={handleConfirm}>
                  <i className="fa fa-check mr-1"></i>
                  Confirm & Analyze
                </Button>
                <Button color="primary" data-color={color} onClick={handleCancel}>
                  <i className="fa fa-times mr-1"></i>
                  Cancel
                </Button>
              </CardBody>
            </Card>
          </ModalBody>
        </Modal>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default AnalysisConfirmationDialog;
